import { StepType } from '@reactour/tour';
import TourStepContent from '../TourStepContent';
import SandboxTourNote from '../SandboxTourNote';
import { TourConfig } from '../tourTypes';
import { blockAllNoDim, clickThroughMask, sidebarMenuStep } from '../stepStyles';

// Step indices for the CANpark Manage Sites tour. The gate steps (MENU, ADD_SITE)
// auto-advance via useManageSitesTourSync; the rest are Next-driven.
export const MANAGE_SITES_STEP = {
	WELCOME: 0,
	OPEN_CANPARK: 1,
	MANAGE_SITES_ITEM: 2,
	COMPANY: 3,
	ADD_SITE: 4,
	NAME: 5,
	ZONE: 6,
	VIOLATION: 7,
	WARNING: 8,
	TOW: 9,
	WORKFLOW: 10,
	PREVIEW: 11,
	TOW_INSTRUCTIONS: 12,
	CREATE: 13,
	FINISH: 14,
} as const;

// The new-site form lives in a right-anchored MUI Drawer that scrolls; tell
// reactour to re-measure as it scrolls/mutates so the popover stays anchored.
const siteDrawerObservers = {
	mutationObservables: ['.MuiDrawer-paperAnchorRight'],
	resizeObservables: ['.MuiDrawer-paperAnchorRight'],
};

const steps: StepType[] = [
	{
		selector: 'body',
		position: 'center',
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				title="Welcome to CANpark 🚗"
				body={
					<>
						<span>
							Let’s set up your first parking site together. It
							only takes a minute.
						</span>
						<SandboxTourNote>
							<strong>Note:</strong> This is a sandbox walkthrough.
							No real site will be created.
						</SandboxTourNote>
					</>
				}
			/>
		),
	},
	{
		selector: '[data-tour="canpark-menu-group"]',
		position: 'right',
		...sidebarMenuStep('[data-tour="canpark-menu-group"]'),
		content: (
			<TourStepContent
				awaitAction
				tailPosition="right"
				body="Open the CANpark menu — click it to expand."
			/>
		),
	},
	{
		selector: '[data-tour="manage-sites-menu-item"]',
		position: 'right',
		...sidebarMenuStep('[data-tour="manage-sites-menu-item"]'),
		content: (
			<TourStepContent
				awaitAction
				tailPosition="right"
				hideBack
				body="Now click Manage Sites."
			/>
		),
	},
	{
		selector: '[data-tour="company-selector"]',
		// Sit ABOVE the field so the popover never covers the company dropdown that
		// opens below it (a position function, so it can't auto-flip back to bottom
		// when the field is near the top). click-through so the operator can open
		// the dropdown, scroll, and actually pick a company.
		position: ({ top, left, right }, prevRect) => [
			(left + right) / 2 - prevRect.width / 2,
			Math.max(8, top - prevRect.height - 12),
		],
		styles: clickThroughMask,
		content: (
			<TourStepContent
				tailPosition="top"
				hideBack
				body="Pick the company you’re configuring — yours is selected by default."
			/>
		),
	},
	{
		selector: '[data-tour="add-parking-site-button"]',
		position: 'bottom',
		content: (
			<TourStepContent
				awaitAction
				tailPosition="left"
				body="Click “Add Parking Site” to open the new-site form."
			/>
		),
	},
	{
		selector: '[data-tour="parking-lot-name-field"]',
		position: 'left',
		// Hug the field: it's full-width to the drawer edge, so the default mask
		// padding spills the spotlight past its edges (out to the screen edge).
		padding: { mask: 4 },
		content: (
			<TourStepContent
				tailPosition="left"
				hideBack
				requires="manage-sites-name"
				body="Give your parking lot a clear name to continue."
			/>
		),
		...siteDrawerObservers,
	},
	{
		selector: '[data-tour="zone-address-fields"]',
		position: 'left',
		content: (
			<TourStepContent
				tailPosition="left"
				requires="manage-sites-zone"
				body="Enter the parking lot’s address to continue — this one’s required. You can optionally add more zones for your enforcers to choose from."
			/>
		),
		...siteDrawerObservers,
	},
	{
		selector: '[data-tour="violation-check-frequency"]',
		position: 'left',
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				tailPosition="left"
				body="Here you can set how far back the system should check for violations. For the tour, we will keep the defualt of Fixed Monthly."
			/>
		),
		...siteDrawerObservers,
	},
	{
		selector: '[data-tour="warning-policy"]',
		position: 'left',
		content: (
			<TourStepContent
				tailPosition="left"
				body="Choose your warning policy — when officers should issue a warning."
			/>
		),
		...siteDrawerObservers,
	},
	{
		selector: '[data-tour="tow-policy"]',
		position: 'top',
		content: (
			<TourStepContent
				tailPosition="top"
				body="Choose your tow policy — when a vehicle becomes eligible to be towed."
			/>
		),
		...siteDrawerObservers,
	},
	{
		selector: '[data-tour="workflow-options"]',
		position: 'left',
		content: (
			<TourStepContent
				tailPosition="left"
				body="Toggle which stages your officers go through while issuing a ticket."
			/>
		),
		...siteDrawerObservers,
	},
	{
		selector: '[data-tour="operator-flow-preview"]',
		position: 'top',
		content: (
			<TourStepContent
				tailPosition="top"
				body="Preview the operator’s flow — it updates live as you change the settings above."
			/>
		),
		...siteDrawerObservers,
	},
	{
		selector: '[data-tour="tow-instructions"]',
		position: 'left',
		content: (
			<TourStepContent
				tailPosition="top"
				body="If you tow, add the towing company and after-tow instructions here (optional)."
			/>
		),
		...siteDrawerObservers,
	},
	{
		selector: '[data-tour="create-button"]',
		// Sit ABOVE the Create button (bottom-left of the drawer) and aim the tail
		// down at it: left-align the box to the button, then a negative tailOffset
		// shifts the centred tail left onto the button.
		position: ({ left, top }, prevRect) => [
			Math.max(8, left),
			Math.max(8, top - prevRect.height - 12),
		],
		content: (
			<TourStepContent
				awaitAction
				tailPosition="top"
				tailOffset={-26}
				body="That’s everything — click Create to add your first parking site!"
			/>
		),
		...siteDrawerObservers,
	},
	{
		// FINISH — reached after the sandboxed create closes the drawer. No real
		// site is created during the tour.
		selector: 'body',
		position: 'center',
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				finalStep
				hideBack
				title="Site ready! 🎉"
				body="That’s how you set up a parking site in CANpark. Nothing was created — this was just a walkthrough."
			/>
		),
	},
];

export const canparkManageSitesTour: TourConfig = {
	steps,
	startRoute: '/',
	allowedRoutes: ['/', '/canpark-admin'],
};
